import { keyBy, without } from "lodash";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CustomField,
  CustomFieldContext,
  CustomFieldType,
  ExtensionOperationContext,
  ExtensionOperationWithExtensionId,
  OrderByItem,
  useCallExtensionOperationMutation,
  useCallBulkExtensionOperationMutation,
  useGetCustomFieldDefinitionsQuery,
  useGetExtensionsOperationsQuery,
  useGetGroupsQuery,
} from "../../../graphql/generated";
import useDialog from "../../../utils/hooks/useDialog";
import ErrorMessage from "../ErrorMessage/ErrorMessage";
import LynksTable from "../LynksTable";
import { LynksTableProps, Sort, TableField } from "../LynksTable/LynksTable";
import { getCustomFieldFilterOptions } from "./customFieldFilterOptions";
import { gridSortModelToApiOrderBy } from "./gridSortModelToApiOrderBy";
import { useTranslation } from "react-i18next";
import LoadingOverlay from "../LoadingOverlay";
import BulkActionModal from "../BulkActionModal";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import DropdownButtonGroup, { ActionMenu } from "../DropdownButtonGroup";
import {
  GridCallbackDetails,
  GridFilterItem,
  GridFilterModel,
  GridRowOrderChangeParams,
} from "@mui/x-data-grid-pro";
import { exportAllTableData } from "../../../utils/export/exportAllTableData";
import { castArray } from "lodash";
import enumLabel from "../../../utils/labels/enumLabel";
import { matchesFilter } from "../../../utils/extensions/filterEvaluation";
import { testIds } from "./SmartLynksTable.testIds";

const DEFAULT_ROWS_COUNT = 25;

type SmartLynksTableQueryVariables<C, V = {}> = {
  search?: string;
  sort?: Sort<C> | null;
  skip?: number;
  take?: number;
  filter?: { [key: string]: any };
  orderBy?: OrderByItem[];
} & V;

type SmartLynksTableQueryOptions = {
  retry?: boolean;
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean;
  refetchOnReconnect?: boolean;
  refetchInterval?: number;
  refetchIntervalInBackground?: boolean;
  refetchOnLimit?: boolean;
  retryDelay?: number;
  staleTime?: number;
  cacheTime?: number;
  useErrorBoundary?: boolean;
  onError?: (error: Error) => void;
  enabled?: boolean;
  suspense?: boolean;
  keepPreviousData?: boolean;
  structuralSharing?: boolean;
};

export type QueryFn<T, K extends string, C = unknown, V = {}> = ((
  variables: SmartLynksTableQueryVariables<C, V>,
  queryOptions?: SmartLynksTableQueryOptions
) => {
  data:
    | {
        [key in K]:
          | {
              data: T[];
              count: number | null;
            }
          | undefined;
      }
    | undefined;
  isLoading: boolean;
  isPending?: boolean;
  isFetching?: boolean;
  isRefetching?: boolean;
  error?: Error | null;
  refetch?: () => Promise<unknown>;
}) & {
  // Some generated query hooks (graphql-codegen) expose a static `fetcher` used for server-side/manual fetching
  fetcher?: (variables?: any, options?: any) => () => Promise<any>;
};

export type MutationFn<V = {}, R = unknown> = () => {
  mutateAsync: (variables: V) => Promise<R>;
  isLoading: boolean;
  error?: Error | null;
};

export type DeleteMutation = MutationFn<{ id: string }>;

export type SmartLynksTableProps<
  T,
  K extends string,
  E extends string | number | symbol = never,
  C extends string = never,
  V = {}
> = {
  fields?: LynksTableProps<T, E, C>["fields"];
  renderRow?: LynksTableProps<T, E, C>["renderRow"];
  renderHeader?: LynksTableProps<T, E, C>["renderHeader"];
  sortFields?: LynksTableProps<T, E, C>["sortFields"];
  actions?: LynksTableProps<T, E, C>["actions"];
  onRecordClick?: LynksTableProps<T, E, C>["onRecordClick"];
  onSelect?: LynksTableProps<T, E, C>["onSelect"];
  selectable?: boolean;
  detailsUrlPrefix?: LynksTableProps<T, E, C>["detailsUrlPrefix"];
  dataKey: K;
  query: QueryFn<T, K, C, V>;
  additionalQueryVariables?: V;
  queryOptions?: SmartLynksTableQueryOptions;
  disableSearch?: boolean;
  disablePagination?: boolean;
  deleteMutation?: DeleteMutation;
  extensionOperationContext?: ExtensionOperationContext;
  id?: LynksTableProps<T, E, C>["id"];
  customFieldContext?: CustomFieldContext[];
  showGroups?: boolean;
  // additionnalsCustomFieldsContext?: CustomFieldContext[];
  customFieldsGetter?: (record: T) => CustomField[];
  getRecordParentId?: LynksTableProps<T, E, C>["getRecordParentId"];
  onFilteredRecordsChange?: LynksTableProps<T, E, C>["onFilteredRecordsChange"];
  bulkActionMenus?: Array<ActionMenu>;
  renderHeaderActions?: () => React.ReactNode;
  getRowHeight?: LynksTableProps<T, E, C>["getRowHeight"];
  getEstimatedRowHeight?: LynksTableProps<T, E, C>["getEstimatedRowHeight"];
  getRowSpacing?: LynksTableProps<T, E, C>["getRowSpacing"];
  filterMode?: LynksTableProps<T, E, C>["filterMode"];
  paginationMode?: LynksTableProps<T, E, C>["paginationMode"];
  sortingMode?: LynksTableProps<T, E, C>["sortingMode"];
  rowReordering?: LynksTableProps<T, E, C>["rowReordering"];
  onRowOrderChange?: LynksTableProps<T, E, C>["onRowOrderChange"];
  disableRowSelectionOnClick?: LynksTableProps<
    T,
    E,
    C
  >["disableRowSelectionOnClick"];
  onFilterModelChange?: (
    model: GridFilterModel,
    details: GridCallbackDetails<"filter">
  ) => void;
  /**
   * Optional function to get a human-readable label for a record.
   * Used in error messages to display meaningful identifiers instead of raw IDs.
   * @param record - The record to get a label for
   * @returns A human-readable string representing the record
   * @example
   * getRecordLabel={(invoice) => invoice.invoiceNumber || invoice._id}
   */
  getRecordLabel?: (record: T) => string;
};

export default function SmartLynksTable<
  T extends {
    _id: string;
    groupIds?: string[] | null;
  },
  K extends string,
  E extends string | number | symbol = never,
  C extends string = never,
  V = {}
>({
  fields,
  renderRow,
  renderHeader,
  sortFields,
  actions,
  query,
  additionalQueryVariables,
  queryOptions,
  dataKey,
  detailsUrlPrefix,
  onRecordClick,
  onSelect,
  selectable,
  disableSearch,
  disablePagination,
  deleteMutation,
  extensionOperationContext,
  id,
  customFieldContext = [],
  showGroups,
  customFieldsGetter,
  getRecordParentId,
  onFilteredRecordsChange,
  bulkActionMenus,
  renderHeaderActions,
  getRowHeight,
  getEstimatedRowHeight,
  getRowSpacing,
  filterMode,
  paginationMode,
  sortingMode,
  rowReordering,
  onRowOrderChange,
  disableRowSelectionOnClick,
  onFilterModelChange,
  getRecordLabel,
}: SmartLynksTableProps<T, K, E, C, V>) {
  const { t } = useTranslation("common");
  const [take, setTake] = useState(DEFAULT_ROWS_COUNT);
  const [skip, setSkip] = useState(0);
  const [sort, setSort] = useState<Sort<C> | null>(null);
  const [selected, setSelected] = useState<T[]>([]);
  const [loadingRecordIds, setLoadingRecordIds] = useState<string[]>([]);
  const [isBulkActionModalOpen, setIsBulkActionModalOpen] = useState(false);
  const [filter, setFilter] = useState<{ [key: string]: any }>({});
  const [search, setSearch] = useState<string | null>(null);
  const [orderBy, setOrderBy] = useState<OrderByItem[]>([]);
  const [confirmationDialog, setConfirmationDialog] =
    useState<{
      open: boolean;
      operation: ExtensionOperationWithExtensionId | null;
      recordIds: string[];
      recordLabels: string[];
    } | null>(null);

  const { showDialog, hideDialog } = useDialog();
  const { enqueueSnackbar } = useSnackbar();

  /**
   * Helper function to get a human-readable label for a record.
   * Uses the getRecordLabel prop if provided, otherwise falls back to the record ID.
   * @param recordId - The ID of the record to get a label for
   * @returns A human-readable string representing the record
   */
  const getRecordLabelOrId = useCallback(
    (recordId: string): string => {
      if (getRecordLabel) {
        const record = selected.find((item) => item._id === recordId);
        if (record) {
          return getRecordLabel(record);
        }
      }
      return recordId;
    },
    [getRecordLabel, selected]
  );

  const groupsQuery =
    process.env.NODE_ENV !== "test"
      ? // eslint-disable-next-line react-hooks/rules-of-hooks
        useGetGroupsQuery(
          {},
          {
            enabled: !!showGroups,
          }
        )
      : null;
  const groupsData = useMemo(
    () => groupsQuery?.data?.groups.data || [],
    [groupsQuery?.data]
  );
  const groupMap = useMemo(() => keyBy(groupsData, (g) => g._id), [groupsData]);

  const queryResult = query(
    //@ts-ignore additionalQueryVariables will always be compatible
    // with query variables (type V) but somehow ts does not see it that way
    {
      sort,
      take,
      skip,
      filter,
      search,
      orderBy,
      ...(additionalQueryVariables || {}),
    },
    {
      retry: false,
      ...(queryOptions || {}),
      keepPreviousData: true,
    }
  );

  const deleteMutationHandle = deleteMutation ? deleteMutation() : null;

  // const previousSelected = usePrevious(selected);
  // useEffect(() => {
  //   // avoid resetting page when user select a load
  //   if (!selected.length && previousSelected?.length !== 1) {
  //     setSkip(0);
  //   }
  //   // we do not want to reset the page when selected loads change
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [sort, additionalQueryVariables]);

  const data = useMemo(() => {
    return queryResult.data ? queryResult.data[dataKey]?.data || [] : [];
  }, [queryResult.data, dataKey]);

  const count = useMemo(() => {
    return queryResult.data ? queryResult.data[dataKey]?.count || null : null;
  }, [queryResult.data, dataKey]);

  useEffect(() => {
    setSelected([]);
  }, [data]);

  const extensionsOperationsQuery =
    process.env.NODE_ENV !== "test" && extensionOperationContext
      ? // eslint-disable-next-line react-hooks/rules-of-hooks
        useGetExtensionsOperationsQuery({
          context: extensionOperationContext,
        })
      : null;
  const extensionOperations = useMemo(
    () => extensionsOperationsQuery?.data?.extensionsOperations || [],
    [extensionsOperationsQuery?.data?.extensionsOperations]
  );

  const callExtensionOperationMutation =
    process.env.NODE_ENV !== "test"
      ? // eslint-disable-next-line react-hooks/rules-of-hooks
        useCallExtensionOperationMutation()
      : null;

  const callBulkExtensionOperationMutation =
    process.env.NODE_ENV !== "test"
      ? // eslint-disable-next-line react-hooks/rules-of-hooks
        useCallBulkExtensionOperationMutation()
      : null;

  const customFieldDefinitionsQuery =
    customFieldContext.length && process.env.NODE_ENV !== "test"
      ? // eslint-disable-next-line react-hooks/rules-of-hooks
        useGetCustomFieldDefinitionsQuery()
      : null;

  const customFieldDefinitions =
    customFieldDefinitionsQuery?.data?.cutomFieldDefinitions.data
      ?.filter(
        (customFieldDefinition) =>
          customFieldContext.length &&
          customFieldDefinition.context.some((ctx) =>
            customFieldContext.includes(ctx)
          )
      )
      ?.filter(
        (def, index, arr) => arr.findIndex((d) => d.key === def.key) === index
      );

  // Create custom fields as TableFields for export
  const customFieldsAsTableFields = useMemo(() => {
    if (!customFieldDefinitions) return [];

    return customFieldDefinitions.map((customFieldDefinition): TableField<
      T,
      any
    > => {
      return {
        value: (row: T) => {
          const computedValue = (
            customFieldsGetter?.(row) || (row as any).customFields
          )?.find(
            (customField: any) => customField.key === customFieldDefinition.key
          )?.value;

          if (!computedValue) return "";

          // Handle different custom field types for export
          if (customFieldDefinition.type === CustomFieldType.Select) {
            return enumLabel(computedValue);
          }
          if (customFieldDefinition.type === CustomFieldType.Multiselect) {
            return castArray(computedValue).map(enumLabel).join(", ");
          }
          if (
            customFieldDefinition.type === CustomFieldType.Date ||
            customFieldDefinition.type === CustomFieldType.Datetime
          ) {
            return computedValue ? new Date(computedValue) : null;
          }
          return computedValue;
        },
        label: customFieldDefinition.label,
        type:
          customFieldDefinition.type === CustomFieldType.Select ||
          customFieldDefinition.type === CustomFieldType.Multiselect
            ? "select"
            : customFieldDefinition.type === CustomFieldType.Number
            ? "number"
            : customFieldDefinition.type === CustomFieldType.Boolean
            ? "boolean"
            : "string",
        // Give select fields their options so the filter dropdown is usable
        values: getCustomFieldFilterOptions(customFieldDefinition),
        sortBy: `customFields.${customFieldDefinition.key}`,
      };
    });
  }, [customFieldDefinitions, customFieldsGetter]);

  const augmentedFields = useMemo(() => {
    const groupsField: TableField<T> = {
      value: (row: T) =>
        (row?.groupIds || [])
          .map((id) => groupMap[id])
          .filter(Boolean)
          .map((g) => g.name),
      rawValue: (row: T) =>
        (row?.groupIds || [])
          .map((id) => groupMap[id])
          .filter(Boolean)
          .map((g) => g.name)
          .join(", "),
      label: t("groups"),
      type: "select",
      values: groupsData.map((g) => ({
        value: g._id,
        label: g.name,
      })),
      sortBy: "groupIds",
    };

    // Start with base fields
    let allFields = fields || [];

    // Add groups field if needed
    if (showGroups) {
      allFields = allFields.concat(groupsField);
    }

    // Add custom fields
    allFields = allFields.concat(
      customFieldsAsTableFields as TableField<T, any>[]
    );

    return allFields;
  }, [fields, showGroups, groupMap, groupsData, t, customFieldsAsTableFields]);

  /**
   * Execute bulk extension operation after user confirmation.
   * This function is called when the user confirms the operation in the confirmation dialog.
   */
  const executeBulkOperation = useCallback(async () => {
    if (!confirmationDialog?.operation) return;

    const { operation, recordIds } = confirmationDialog;

    // Close confirmation dialog
    setConfirmationDialog(null);

    try {
      if (!callBulkExtensionOperationMutation) {
        return;
      }

      // Show loading state
      setLoadingRecordIds(recordIds);

      const response = await callBulkExtensionOperationMutation.mutateAsync({
        extensionId: operation.extensionId,
        operationKey: operation.key,
        recordIds,
      });

      // Display results
      const { successCount, failureCount, message, results } =
        response.callBulkExtensionOperation;

      if (failureCount === 0) {
        // All succeeded - display success snackbar
        enqueueSnackbar(
          message || `Successfully processed ${successCount} records`,
          { variant: "success" }
        );
      } else if (successCount === 0) {
        // All failed - display error snackbar
        enqueueSnackbar(
          message || `Failed to process ${failureCount} records`,
          { variant: "error" }
        );
      } else {
        // Partial success - display warning snackbar
        enqueueSnackbar(
          `Processed ${successCount} records successfully, ${failureCount} failed`,
          { variant: "warning" }
        );
      }

      // Show detailed errors if any failures occurred
      const failedResults = results.filter((r) => !r.success);
      if (failedResults.length > 0) {
        // Use human-readable labels in error dialog
        showDialog({
          title: "Bulk Operation Errors",
          description: (
            <div>
              <p>{failedResults.length} record(s) failed:</p>
              <ul style={{ maxHeight: "400px", overflow: "auto" }}>
                {failedResults.map((result) => {
                  const recordLabel = getRecordLabelOrId(result.recordId);
                  return (
                    <li key={result.recordId}>
                      <strong>{recordLabel}:</strong>{" "}
                      {result.error || result.message || "Failed"}
                    </li>
                  );
                })}
              </ul>
            </div>
          ),
          type: "error",
        });
      }

      // Refresh table data after operation completes
      queryResult?.refetch?.();
    } catch (error) {
      const errorMessage = (error as Error).message || "Unknown error occurred";

      showDialog({
        title: t("error.title", "Error"),
        description: errorMessage,
        type: "error",
      });
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      // Always clear loading state, even if an error occurred
      setLoadingRecordIds([]);
    }
  }, [
    confirmationDialog,
    callBulkExtensionOperationMutation,
    enqueueSnackbar,
    showDialog,
    t,
    getRecordLabelOrId,
    queryResult,
  ]);

  // Map extension operations to bulk action menu items
  const extensionBulkActions: ActionMenu[] = useMemo(() => {
    return extensionOperations.map((extensionOperation) => {
      // Check if all selected records match the filter
      const allRecordsMatch = selected.every((record) =>
        matchesFilter(record, extensionOperation.filter)
      );

      return {
        id: `extension-${extensionOperation.key}`,
        icon: extensionOperation.icon ? (
          <img
            src={extensionOperation.icon}
            alt={extensionOperation.description}
            style={{ width: 20, height: 20 }}
          />
        ) : undefined,
        label: extensionOperation.name,
        disabled: !allRecordsMatch, // Disable if any record doesn't match
        onClick: async () => {
          // Defensive check: prevent execution if any record doesn't match
          if (!allRecordsMatch) {
            enqueueSnackbar(
              `This action is not available for all selected records`,
              { variant: "warning" }
            );
            return;
          }

          if (selected.length === 0) {
            showDialog({
              title: t("common:list.selectItemFirst", "Select an item first"),
              description: t(
                "common:list.selectBulkError",
                "Please select at least one item to use bulk actions"
              ),
              type: "primary",
            });
            return;
          }

          // Show confirmation dialog before executing
          const recordIds = selected.map((item) => item._id);
          const recordLabels = recordIds.map(getRecordLabelOrId);

          setConfirmationDialog({
            open: true,
            operation: extensionOperation,
            recordIds,
            recordLabels,
          });
        },
      };
    });
  }, [
    extensionOperations,
    selected,
    showDialog,
    t,
    enqueueSnackbar,
    getRecordLabelOrId,
  ]);

  // Map extension operations to single-record actions with filter evaluation
  const extensionActions = useMemo(() => {
    return extensionOperations.map((extensionOperation) => ({
      icon: (
        <img
          src={extensionOperation.icon}
          alt={extensionOperation.description}
          style={{
            width: 20,
            height: 20,
          }}
        />
      ),
      tooltip: extensionOperation.name,
      // Evaluate filter to determine if action should be disabled
      disabled: (item: T) => !matchesFilter(item, extensionOperation.filter),
      onClick: async (item: T) => {
        // Prevent execution if filter doesn't match (defensive check)
        if (!matchesFilter(item, extensionOperation.filter)) {
          enqueueSnackbar(`This action is not available for this record`, {
            variant: "warning",
          });
          return;
        }

        try {
          if (!callExtensionOperationMutation) {
            return;
          }
          setLoadingRecordIds(loadingRecordIds.concat(item._id));
          const response = await callExtensionOperationMutation.mutateAsync({
            extensionId: extensionOperation.extensionId,
            operationKey: extensionOperation.key,
            recordId: item._id,
          });
          if (response.callExtensionOperation.message) {
            enqueueSnackbar(response.callExtensionOperation.message);
          }
          if (response.callExtensionOperation.redirect) {
            if (response.callExtensionOperation.openNewWindow) {
              window.open(
                response.callExtensionOperation.redirect,
                `${item._id}::${extensionOperation.extensionId}::${extensionOperation.key}`,
                `scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=992,height=680,left=100,top=100`
              );
            } else {
              window.location.href = response.callExtensionOperation.redirect;
            }
          }
        } catch (error) {
          const errorMessage =
            (error as Error).message || "Unknown error occurred";

          showDialog({
            title: t("error.title", "Error"),
            description: errorMessage,
            type: "error",
          });
          enqueueSnackbar(errorMessage, { variant: "error" });
        } finally {
          // Always clear loading state, even if an error occurred
          setLoadingRecordIds(without(loadingRecordIds, item._id));
        }
      },
    }));
  }, [
    extensionOperations,
    callExtensionOperationMutation,
    enqueueSnackbar,
    showDialog,
    t,
    loadingRecordIds,
  ]);

  // Combine custom actions with extension actions
  const allActions = useMemo(() => {
    return (actions || []).concat(extensionActions);
  }, [actions, extensionActions]);

  // Merge extension bulk actions with existing bulk action menus
  const allBulkActions = useMemo(() => {
    return [...(bulkActionMenus || []), ...extensionBulkActions];
  }, [bulkActionMenus, extensionBulkActions]);

  // Export handler - fetches all data and exports to CSV
  const handleExport = useCallback(async () => {
    if (!augmentedFields?.length) return;
    if (!query.fetcher) {
      enqueueSnackbar("Export not available for this table", {
        variant: "error",
      });
      return;
    }

    try {
      enqueueSnackbar("Preparing export...", { variant: "info" });

      await exportAllTableData({
        fetcher: query.fetcher,
        variables: {
          filter,
          search,
          orderBy,
          ...(additionalQueryVariables || {}),
        },
        dataKey,
        fields: augmentedFields as TableField<T, any>[],
        filename: `TrueTMS-${dataKey}.csv`,
        onProgress: (current, total) => {
          if (total && current < total) {
            enqueueSnackbar(
              `Exporting... ${current} of ${total} rows fetched`,
              { variant: "info" }
            );
          }
        },
      });

      enqueueSnackbar("Export complete!", { variant: "success" });
    } catch (error) {
      console.error("Export failed:", error);
      enqueueSnackbar(`Export failed: ${(error as Error).message}`, {
        variant: "error",
      });
    }
  }, [
    augmentedFields,
    query.fetcher,
    filter,
    search,
    orderBy,
    additionalQueryVariables,
    dataKey,
    enqueueSnackbar,
  ]);

  if (
    customFieldDefinitionsQuery?.isLoading ||
    (showGroups && groupsQuery?.isLoading)
  ) {
    return <LoadingOverlay loading />;
  }

  return (
    <>
      <ErrorMessage error={queryResult.error} />
      {isBulkActionModalOpen && (
        <BulkActionModal
          entityIds={selected.map((entity) => entity._id)}
          onSubmitComplete={() => queryResult?.refetch?.()}
          dataKey={dataKey}
          open={isBulkActionModalOpen}
          onClose={() => setIsBulkActionModalOpen(false)}
        />
      )}
      {confirmationDialog && (
        <Dialog
          open={confirmationDialog.open}
          onClose={() => setConfirmationDialog(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {t("extensions.confirmBulkOperation", "Confirm Bulk Operation")}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              {t(
                "extensions.confirmBulkOperationDescription",
                "You are about to execute the following operation:"
              )}
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              {confirmationDialog.operation?.name}
            </Typography>
            {confirmationDialog.operation?.description && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {confirmationDialog.operation.description}
              </Typography>
            )}
            <Typography variant="body1" sx={{ mt: 2, mb: 1 }}>
              {t(
                "extensions.affectedRecords",
                "This will affect {{count}} record(s):",
                { count: confirmationDialog.recordLabels.length }
              )}
            </Typography>
            <Box
              sx={{
                maxHeight: 200,
                overflow: "auto",
                bgcolor: "background.default",
                p: 2,
                borderRadius: 1,
              }}
            >
              {confirmationDialog.recordLabels
                .slice(0, 10)
                .map((label, index) => (
                  <Typography key={index} variant="body2">
                    • {label}
                  </Typography>
                ))}
              {confirmationDialog.recordLabels.length > 10 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {t("extensions.andMoreRecords", "...and {{count}} more", {
                    count: confirmationDialog.recordLabels.length - 10,
                  })}
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              data-testid={testIds.smartLynksTableCancel}
              onClick={() => setConfirmationDialog(null)}
              color="inherit"
            >
              {t("cancel", "Cancel")}
            </Button>
            <Button
              data-testid={testIds.smartLynksTableConfirm}
              onClick={executeBulkOperation}
              color="primary"
              variant="contained"
              autoFocus
            >
              {t("confirm", "Confirm")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <Stack
        direction="row"
        justifyContent={
          (selectable || onSelect) && selected.length > 0
            ? "space-between"
            : "flex-end"
        }
        spacing={2}
        mb={2}
      >
        {(selectable || onSelect) && selected.length > 0 && (
          <DropdownButtonGroup
            color="secondary"
            variant="outlined"
            size="large"
            menus={[
              {
                id: "edit",
                icon: <Edit />,
                label: t("list.edit"),
                onClick: () => {
                  if (selected.length === 0) {
                    showDialog({
                      title: t(
                        "common:list.selectItemFirst",
                        "Select an item first"
                      ),
                      description: t(
                        "common:list.selectBulkError",
                        "Please select at least one item to use bulk actions"
                      ),
                      type: "primary",
                    });
                    return;
                  }
                  setIsBulkActionModalOpen(true);
                },
              },
              ...allBulkActions,
            ]}
            id="bulk-action-buttons"
          />
        )}
        <Stack direction="row" spacing={2}>
          {renderHeaderActions ? renderHeaderActions() : null}
        </Stack>
      </Stack>
      {/* @ts-expect-error */}
      <LynksTable
        data={data}
        fields={renderRow ? undefined : augmentedFields}
        renderRow={renderRow}
        renderHeader={renderHeader}
        sortFields={sortFields}
        loadingRecordIds={loadingRecordIds}
        actions={allActions}
        loading={queryResult.isLoading}
        detailsUrlPrefix={detailsUrlPrefix}
        disableSearch={disableSearch}
        onSortChange={setSort}
        count={disablePagination ? null : count}
        page={skip / take + 1}
        perPage={disablePagination ? undefined : take}
        rowReordering={rowReordering !== undefined ? true : undefined}
        onRowOrderChange={
          rowReordering
            ? (onRowOrderChange as
                | ((params: GridRowOrderChangeParams) => void)
                | undefined)
            : undefined
        }
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        {...(rowReordering === false
          ? {
              sx: {
                // rowReordering is always passed as true so the column stays
                // rendered, but we visually disable the drag handle via CSS
                // when the caller has set rowReordering=false.
                ".MuiDataGrid-rowReorderCell": {
                  opacity: 0.3,
                  cursor: "default",
                  pointerEvents: "none",
                },
              },
            }
          : {})}
        onRecordClick={onRecordClick}
        onSelect={
          onSelect || selectable
            ? (selected) => {
                setSelected(selected);
                onSelect?.(selected);
              }
            : undefined
        }
        onDelete={
          deleteMutationHandle
            ? async (id) => {
                showDialog({
                  title: t("error.dangerZone", "Danger zone"),
                  description: t(
                    "error.deleteConfirmation",
                    "Do you really want to delete this? This action cannot be undone."
                  ),
                  type: "error",
                  actions: [
                    {
                      type: "primary",
                      title: t("error.noCancel", "No, Cancel"),
                    },
                    {
                      type: "error",
                      title: t("error.yesDelete", "Yes, Delete"),
                      onClick: async () => {
                        try {
                          setLoadingRecordIds(loadingRecordIds.concat(id));

                          await deleteMutationHandle.mutateAsync({
                            id,
                          });
                          hideDialog();
                          if (queryResult.refetch) {
                            queryResult.refetch();
                          }
                        } catch (error) {
                          console.error(error);
                          showDialog({
                            title: t("error.title", "Error"),
                            description:
                              t(
                                "error.deleteError",
                                "An error occurred while deleting."
                              ) +
                              (deleteMutationHandle.error?.message ||
                                t("error.unknownError", "Unknown error")),
                          });
                        } finally {
                          setLoadingRecordIds(without(loadingRecordIds, id));
                        }
                      },
                    },
                  ],
                });
              }
            : undefined
        }
        id={id || dataKey}
        getRecordParentId={getRecordParentId}
        onFilteredRecordsChange={onFilteredRecordsChange}
        filterMode={filterMode || "server"}
        onFilterModelChange={(model, details) => {
          setSearch(model.quickFilterValues?.[0] || null);
          setFilter(gridFilterModelToApiFilter(model, details));
          onFilterModelChange?.(model, details);
        }}
        paginationMode={paginationMode || "server"}
        onPaginationModelChange={(pagination) => {
          setTake(pagination.pageSize);
          setSkip(pagination.page * pagination.pageSize);
        }}
        sortingMode={sortingMode || "server"}
        onSortModelChange={(model, details) => {
          setOrderBy(gridSortModelToApiOrderBy(model, details));
        }}
        rowCount={count || 0}
        onExport={handleExport}
        getRowHeight={getRowHeight}
        getEstimatedRowHeight={getEstimatedRowHeight}
        getRowSpacing={getRowSpacing}
      />
    </>
  );
}

type ApiFilter = {
  [key: string]: any;
};

const operatorRequiresValue = (operator: string): boolean => {
  switch (operator) {
    case "isEmpty":
    case "isNotEmpty":
      return false;
    default:
      return true;
  }
};

export const gridFilterModelToApiFilter = (
  model: GridFilterModel,
  details: GridCallbackDetails<"filter">
): ApiFilter => {
  const itemsWithoutEmptyValues = model.items.filter(
    (item) =>
      !operatorRequiresValue(item.operator) ||
      (item.value !== "" && item.value !== null && item.value !== undefined)
  );
  if (!itemsWithoutEmptyValues.length) {
    return {};
  }
  return {
    [model.logicOperator === "and" ? "$and" : "$or"]: itemsWithoutEmptyValues
      .map((item) => {
        const column = details.api.getColumn(item.field);
        if (!column) {
          return null;
        }
        const field = String(column.cellClassName || column.field);
        if (field.startsWith("customFields.")) {
          return {
            customFields: {
              $elemMatch: {
                key: field.replace("customFields.", ""),
                value: {
                  [gridFilterOperatorToApiOperator(item.operator)]:
                    gridFilterValueToApiValue(item),
                },
              },
            },
          };
        }
        // The picker has no seconds, so match the whole picked minute.
        if (
          column.type === "dateTime" &&
          (item.operator === "is" || item.operator === "not")
        ) {
          const start = new Date(item.value);
          start.setSeconds(0, 0);
          const minute = {
            $gte: start,
            $lt: new Date(start.getTime() + 60 * 1000),
          };
          return {
            [field]: item.operator === "is" ? minute : { $not: minute },
          };
        }
        const operator = gridFilterOperatorToApiOperator(item.operator);
        return {
          [String(column.cellClassName || column.field)]: {
            [operator]: gridFilterValueToApiValue(item),
            ...(operator === "$regex" ? { $options: "i" } : {}),
          },
        };
      })
      .filter(Boolean),
  };
};

const gridFilterOperatorToApiOperator = (operator: string): string => {
  switch (operator) {
    case "equals":
    case "is":
    case "=":
      return "$eq";
    case "notEquals":
    case "not":
    case "!=":
      return "$ne";
    case "contains":
      return "$regex";
    case "notContains":
      return "$not";
    case "startsWith":
      return "$regex";
    case "endsWith":
      return "$regex";
    case ">":
    case "after":
      return "$gt";
    case "<":
    case "before":
      return "$lt";
    case ">=":
    case "onOrAfter":
      return "$gte";
    case "<=":
    case "onOrBefore":
      return "$lte";
    case "isEmpty":
      return "$eq";
    case "isNotEmpty":
      return "$ne";
    case "isAnyOf":
      return "$in";
    default:
      return "$eq";
  }
};

const gridFilterValueToApiValue = (item: GridFilterItem): any => {
  if (item.operator === "contains") {
    return `.*${item.value}.*`;
  }
  if (item.operator === "isEmpty" || item.operator === "isNotEmpty") {
    return null;
  }

  if (["=", ">", "<", ">=", "<="].includes(item.operator)) {
    return Number(item.value);
  }

  if (item.value === "true" || item.value === "false") {
    return item.value === "true";
  }

  if (item.operator === "startsWith") {
    return `^${item.value}`;
  }

  if (item.operator === "endsWith") {
    return `${item.value}$`;
  }

  return item.value;
};
